import { Component, OnInit, OnChanges, OnDestroy, SimpleChanges, ChangeDetectionStrategy, inject, ChangeDetectorRef, Input, Output, EventEmitter, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CreateToolCommand, UpdateToolCommand, GetToolResult, CreateToolResult, CreateResult, TestToolCommand, TestToolResult, AgentToolDto } from '@generated/models';
import { ToolsService } from '@generated/services';
import { NotificationService } from '@core/services/notification.service';
import { Signalr } from '@core/services/signalr';
import { AssistantContextService } from '@shared/services/assistant-context.service';
import { PropertyDefinition } from '@shared/models/property-schema';
import { OpenapiPropertiesEditorComponent } from '@shared/components/openapi-properties-editor/openapi-properties-editor';
import { openApiSchemaToProperties, propertiesToOpenApiSchema } from '@shared/utils/openapi-schema-utils';


// PrimeNG imports
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { TextareaModule } from 'primeng/textarea';
import { SelectModule } from 'primeng/select';
import { CardModule } from 'primeng/card';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { MenuModule } from 'primeng/menu';
import { Menu } from 'primeng/menu';
import { ChipModule } from 'primeng/chip';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { DividerModule } from 'primeng/divider';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { AccordionModule } from 'primeng/accordion';
import { ToolbarModule } from 'primeng/toolbar';
import { TooltipModule } from 'primeng/tooltip';
import { AssistantSurfaceDirective } from '@shared/directives/assistant-surface.directive';
import { AssistantEditableDirective } from '@shared/directives/assistant-editable.directive';
import { AssistantActionDirective } from '@shared/directives/assistant-action.directive';
import { TOOL_FIELDS } from '@core/assistant/assistant-catalog';



@Component({
  selector: 'app-webhook-tool',
  standalone: true,
  imports: [
    AssistantActionDirective,
    AssistantSurfaceDirective,
    AssistantEditableDirective,
    CommonModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    TextareaModule,
    SelectModule,
    CardModule,
    BreadcrumbModule,
    MenuModule,
    ChipModule,
    ToggleButtonModule,
    ToggleSwitchModule,
    DividerModule,
    ProgressSpinnerModule,
    AccordionModule,
    ToolbarModule,
    TooltipModule,
    OpenapiPropertiesEditorComponent
  ],
  templateUrl: './webhook-tool.html',
  styleUrl: './webhook-tool.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WebhookToolComponent implements OnInit, OnChanges, OnDestroy {
  /** Идентификаторы полей для агента: опечатку ловит компилятор. */
  protected readonly F = TOOL_FIELDS;

  tool: GetToolResult = this.getDefaultTool();
  @Input() isEmbedded = false;
  @Input() toolId: string | null = null;
  /**
   * Agent ↔ tool binding (only set when embedded inside agent-edit).
   * Toggling fields like `async` on this object mutates the agent's
   * tools[] entry in place — persisted when the agent itself is saved.
   * The tool body / endpoint / etc. above still flushes through the
   * standalone tool save path (toolSaved emit).
   */
  @Input() agentTool?: AgentToolDto;
  @Output() toolSaved = new EventEmitter<GetToolResult>();
  @Output() cancelled = new EventEmitter<void>();

  // Режим работы компонента
  get isUpdateMode(): boolean {
    return this.toolId !== null && this.toolId !== undefined;
  }

  get isCreateMode(): boolean {
    return !this.isUpdateMode;
  }


  // @ViewChild('urlInput', { static: false }) urlInput!: ElementRef<HTMLInputElement>;

  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private toolsService = inject(ToolsService);
  private notificationService = inject(NotificationService);
  private cdr = inject(ChangeDetectorRef);
  private signalr = inject(Signalr);
  private destroyRef = inject(DestroyRef);
  private assistantContext = inject(AssistantContextService);


  // HTTP method options
  httpMethodOptions = [
    { label: 'GET', value: 'GET' },
    { label: 'POST', value: 'POST' },
    { label: 'PUT', value: 'PUT' },
    { label: 'PATCH', value: 'PATCH' },
    { label: 'DELETE', value: 'DELETE' }
  ];

  // OpenAPI properties for headers, url_parameters, body
  headersProperties: PropertyDefinition[] = [];
  urlParametersProperties: PropertyDefinition[] = [];
  bodyProperties: PropertyDefinition[] = [];

  readonly headersTitle = $localize`:@@webhookTool.headers:Request Headers`;
  readonly urlParametersTitle = $localize`:@@webhookTool.urlParameters:URL Parameters`;
  readonly bodyTitle = $localize`:@@webhookTool.body:Request Body`;

  get saveChangesBtnLabel(): string {
    if (this.isSaving) return $localize`:@@common.saving:Saving...`;
    return this.hasChanges
      ? $localize`:@@common.save:Save`
      : $localize`:@@common.noChanges:No Changes`;
  }

  breadcrumbItems: any[] = [];
  isSaving = false;
  isLoading = false;
  isTestingTool = false;

  // originalData: any = {};

  private getDefaultTool(): GetToolResult {
    return {
      title: '',
      description: '',
      method: 'GET',
      url: '',
      response_timeout_seconds: 30,
      disable_interruptions: false,
      pre_tool_speech: null,
      progress_speech: null,
      progress_speech_interval_seconds: null
    };
  }

  // TODO: Будет переделано с использованием OpenAPI схем
  // private mapBodyParameter(b: any): any {
  //   return {
  //     identifier: b.identifier || '',
  //     data_type: b.data_type || 'String',
  //     value_type: b.value_type || 'ConstantValue',
  //     required: b.required || false,
  //     description: b.description,
  //     parent_id: b.parent_id,
  //     constant_string: b.constant_string,
  //     constant_int: b.constant_int,
  //     constant_decimal: b.constant_decimal,
  //     constant_bool: b.constant_bool,
  //     children: b.children?.map((child: any) => this.mapBodyParameter(child)) || []
  //   };
  // }

  ngOnInit(): void {
    this.initializeComponent();

    // ToolUpdated прилетает с GetToolResult (та же модель что в state) — кладём напрямую.
    this.signalr.toolUpdated$
      .pipe(
        filter(t => !!this.toolId && t.id === this.toolId),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(updated => {
        this.tool = updated;
        this.loadPropertiesFromSchemas();
        this.publishAssistantContext();
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.assistantContext.clear();
  }

  private publishAssistantContext(): void {
    if (!this.toolId) return;
    this.assistantContext.set({
      description: 'Tool editor',
      value: { type: 'tool', id: this.toolId, title: this.tool.title },
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log('🔧 WebhookTool ngOnChanges called:', changes);
    console.log('🔧 Current toolId:', this.toolId);
    
    if (changes['tool'] && changes['tool'].currentValue) {
      console.log('🔧 Tool changed, reinitializing...');
      this.initializeComponent();
    }
    if (changes['toolId']) {
      console.log('🔧 ToolId changed from', changes['toolId'].previousValue, 'to', changes['toolId'].currentValue);
      this.initializeComponent();
    }
  }

  private initializeComponent(): void {
    console.log('🔧 WebhookTool initializeComponent called');
    console.log('🔧 isEmbedded:', this.isEmbedded, 'toolId:', this.toolId);
    
    if (!this.isEmbedded) {
      // Standalone mode - check if we need to load from server
      this.toolId = this.route.snapshot.paramMap.get('id');
            
      if (this.isUpdateMode && this.toolId) {
        this.refresh(this.toolId);
      }
      
      this.initializeBreadcrumb();
    } else {
      // Embedded mode - determine mode based on tool data
      this.determineEmbeddedMode();
    }

    // Initialize OpenAPI properties from schemas
    this.loadPropertiesFromSchemas();
  }

  private loadPropertiesFromSchemas(): void {
    // Load headers
    if (this.tool.headers_open_api_schema) {
      this.headersProperties = openApiSchemaToProperties(this.tool.headers_open_api_schema);
    } else {
      this.headersProperties = [];
    }

    // Load URL parameters
    if (this.tool.url_parameters_open_api_schema) {
      this.urlParametersProperties = openApiSchemaToProperties(this.tool.url_parameters_open_api_schema);
    } else {
      this.urlParametersProperties = [];
    }

    // Load body
    if (this.tool.body_open_api_schema) {
      this.bodyProperties = openApiSchemaToProperties(this.tool.body_open_api_schema);
    } else {
      this.bodyProperties = [];
    }
  }

  private determineEmbeddedMode(): void {
    console.log('🔧 determineEmbeddedMode called, toolId:', this.toolId);
    // In embedded mode, determine if we're editing an existing tool or creating new
    if (this.toolId) {
      console.log('🔧 Loading tool by ID in embedded mode:', this.toolId);
      this.refresh(this.toolId);
    } else {
      console.log('🔧 Creating new tool in embedded mode');
      this.tool = this.getDefaultTool();
    }
  }


  private refresh(id: string): void {
    console.log('Loading tool by ID:', id);
    this.isLoading = true;
    
    this.toolsService.apiToolsIdGet({ id }).subscribe({
      next: (response: GetToolResult) => {
        console.log('Loaded tool from API:', response);
        this.tool = response;
        this.loadPropertiesFromSchemas();
        this.publishAssistantContext();
        // Крошка строится до загрузки — пересобираем, чтобы в ней
        // появилось название инструмента.
        this.initializeBreadcrumb();
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: (error: any) => {
        console.error('Error loading tool:', error);
        this.notificationService.showError($localize`:@@webhookTool.notify.loadFail:Failed to load tool: ${error.message}:message:`);
        this.isLoading = false;
        this.cdr.markForCheck();
        // Navigate back to tools list on error
        if (!this.isEmbedded) {
          this.router.navigate(['/tools']);
        }
      }
    });
  }





  private initializeBreadcrumb(): void {
    this.breadcrumbItems = [
      { label: $localize`:@@nav.tools:Tools`, routerLink: '/tools' },
      {
        // Показываем название самого инструмента, как agent-edit
        // показывает имя агента. Пока тул не загрузился (или ещё не
        // назван) — фоллбэк на общую подпись.
        label: this.isUpdateMode
          ? (this.tool?.title?.trim() || $localize`:@@webhookTool.breadcrumb.edit:Edit Tool`)
          : (this.tool?.title?.trim() || $localize`:@@webhookTool.breadcrumb.new:New Tool`)
      }
    ];
  }

  onFieldChange(field: string): void {
    this.cdr.markForCheck();
  }

  // private storeOriginalData(): void {
  //   this.originalData = {
  //     name: this.tool.name,
  //     description: this.tool.description,
  //     method: this.tool.method,
  //     endpoint: this.tool.endpoint,
  //     timeout: this.tool.timeout,
  //     headers: JSON.parse(JSON.stringify(this.tool.headers)),
  //     dynamic_variables: JSON.parse(JSON.stringify(this.tool.dynamic_variables)),
  //   };
  //   this.modifiedFields.clear();
  // }


  onSave(): void {
    if (this.isSaving) return;

    // Validate form
    if (!this.tool.title?.trim()) {
      this.notificationService.showError($localize`:@@webhookTool.notify.nameRequired:Please enter a tool name`);
      return;
    }

    if (!this.tool.url?.trim()) {
      this.notificationService.showError($localize`:@@webhookTool.notify.urlRequired:Please enter a URL`);
      return;
    }

    // TODO: Validate OpenAPI schemas for headers, url_parameters, body

    // Always save to API, but emit event in embedded mode after saving
    this.saveTool(this.tool);
  }

  onTestTool(): void {
    if (this.isTestingTool) return;

    // Validate that tool has required fields
    if (!this.tool.url?.trim()) {
      this.notificationService.showError($localize`:@@webhookTool.notify.urlRequiredTest:Please enter a URL before testing`);
      return;
    }

    // Build test command from current tool data
    const testCommand: TestToolCommand = {
      url: this.tool.url,
      method: this.tool.method,
      headers_open_api_schema: this.tool.headers_open_api_schema,
      url_parameters_open_api_schema: this.tool.url_parameters_open_api_schema,
      body_open_api_schema: this.tool.body_open_api_schema,
      response_timeout_seconds: this.tool.response_timeout_seconds
    };

    this.isTestingTool = true;
    this.cdr.markForCheck();

    this.toolsService.apiToolsTestPost({ body: testCommand }).subscribe({
      next: (result: TestToolResult) => {
        this.isTestingTool = false;
        this.cdr.markForCheck();

        if (result.success) {
          this.notificationService.showSuccess($localize`:@@webhookTool.notify.testSuccess:Tool test successful! Status: ${result.status_code}:status:`);
        } else {
          const errMsg = result.error_message || $localize`:@@common.unknownError:Unknown error`;
          this.notificationService.showError($localize`:@@webhookTool.notify.testFailed:Tool test failed: ${errMsg}:error:`);
        }
      },
      error: (error: any) => {
        this.isTestingTool = false;
        this.cdr.markForCheck();
        this.notificationService.showError($localize`:@@webhookTool.notify.testFail:Failed to test tool: ${error.message}:message:`);
      }
    });
  }

  private saveTool(tool: GetToolResult): void {
    console.log('Saving tool:', tool);
    this.isSaving = true;

    // Из единой state-модели GetToolResult вытаскиваем редактируемые поля —
    // серверные (created_at, updated_at, space_id, ...) не отправляем.
    const editable = {
      title: tool.title,
      description: tool.description,
      method: tool.method,
      url: tool.url,
      response_timeout_seconds: tool.response_timeout_seconds,
      headers_open_api_schema: tool.headers_open_api_schema,
      url_parameters_open_api_schema: tool.url_parameters_open_api_schema,
      body_open_api_schema: tool.body_open_api_schema,
      disable_interruptions: tool.disable_interruptions,
      pre_tool_speech: tool.pre_tool_speech,
      progress_speech: tool.progress_speech,
      progress_speech_interval_seconds: tool.progress_speech_interval_seconds,
    };

    if (this.isUpdateMode && this.toolId) {
      const updateTool: UpdateToolCommand = { ...editable, id: this.toolId };
      console.log('Updating tool:', updateTool);

      this.toolsService.apiToolsPut({ body: updateTool }).subscribe({
        next: (response: CreateResult) => {
          console.log('Tool updated successfully:', response);
          this.notificationService.showSuccess($localize`:@@webhookTool.notify.updated:Tool updated successfully`);
          
          this.isSaving = false;
          this.cdr.markForCheck();
          
          // Emit event if in embedded mode - emit server response
          if (this.isEmbedded) {
            this.toolSaved.emit(response);
          }
          
          // Stay on same page - do not navigate away
        },
        error: (error: any) => {
          console.error('Error updating tool:', error);
          this.notificationService.showError($localize`:@@webhookTool.notify.updateFail:Failed to update tool: ${error.message}:message:`);
          this.isSaving = false;
          this.cdr.markForCheck();
        }
      });
    } else {
      const createTool: CreateToolCommand = { ...editable };

      this.toolsService.apiToolsPost({ body: createTool }).subscribe({
        next: (response: CreateToolResult) => {
          console.log('Tool created successfully:', response);
          this.notificationService.showSuccess($localize`:@@webhookTool.notify.created:Tool created successfully`);
          
          // After creating, switch to edit mode with the new tool ID
          if (response.id) {
            this.toolId = response.id;
            
            // Update URL to reflect edit mode without navigation
            window.history.replaceState(null, '', `/tools/${this.toolId}`);
            
            // Update breadcrumb for edit mode
            this.initializeBreadcrumb();
          }
          
          this.isSaving = false;
          this.cdr.markForCheck();
          
          // Emit event if in embedded mode - emit server response with ID
          if (this.isEmbedded) {
            this.toolSaved.emit(response);
          }
          
          // Stay on same page - do not navigate away
        },
        error: (error: any) => {
          console.error('Error creating tool:', error);
          this.notificationService.showError($localize`:@@webhookTool.notify.createFail:Failed to create tool: ${error.message}:message:`);
          this.isSaving = false;
          this.cdr.markForCheck();
        }
      });
    }
  }

  onCancel(): void {
    if (this.isEmbedded) {
      this.cancelled.emit();
    } else {
      this.router.navigate(['/tools']);
    }
  }

  // Handlers for OpenAPI properties changes
  onHeadersPropertiesChange(properties: PropertyDefinition[]): void {
    this.headersProperties = properties;
    this.tool.headers_open_api_schema = propertiesToOpenApiSchema(properties);
    this.cdr.markForCheck();
  }

  onUrlParametersPropertiesChange(properties: PropertyDefinition[]): void {
    this.urlParametersProperties = properties;
    this.tool.url_parameters_open_api_schema = propertiesToOpenApiSchema(properties);
    this.cdr.markForCheck();
  }

  onBodyPropertiesChange(properties: PropertyDefinition[]): void {
    this.bodyProperties = properties;
    this.tool.body_open_api_schema = propertiesToOpenApiSchema(properties);
    this.cdr.markForCheck();
  }

  get headerTitle(): string {
    return this.isUpdateMode
      ? $localize`:@@webhookTool.header.edit:Edit HTTP Tool`
      : $localize`:@@webhookTool.header.create:Create HTTP Tool`;
  }

  get saveButtonLabel(): string {
    return this.isUpdateMode
      ? $localize`:@@webhookTool.updateTool:Update Tool`
      : $localize`:@@webhookTool.createTool:Create Tool`;
  }

  get saveButtonIcon(): string {
    return this.isUpdateMode ? 'pi pi-check' : 'pi pi-plus';
  }

  get hasChanges(): boolean {
    return true; // Always allow saving since we don't track changes
  }

  get isSaveDisabled(): boolean {
    return this.isSaving;
  }

  // Generate curl command preview
  getCurlPreview(): string {
    const lines: string[] = [];

    // Start with curl command
    lines.push(`curl -X ${this.tool.method || 'GET'} \\`);

    // Add URL with query parameters
    let url = this.tool.url || 'https://api.example.com/endpoint';
    if (this.urlParametersProperties && this.urlParametersProperties.length > 0) {
      const params: string[] = [];
      this.urlParametersProperties.forEach(prop => {
        if (prop.fillMode === 'llm') {
          params.push(`${prop.name}=<AI_GENERATED_${prop.type.toUpperCase()}>`);
        } else if (prop.default !== undefined && prop.default !== null) {
          params.push(`${prop.name}=${encodeURIComponent(String(prop.default))}`);
        }
      });
      if (params.length > 0) {
        url += (url.includes('?') ? '&' : '?') + params.join('&');
      }
    }
    lines.push(`  '${url}' \\`);

    // Add headers
    if (this.headersProperties && this.headersProperties.length > 0) {
      this.headersProperties.forEach(prop => {
        if (prop.fillMode === 'llm') {
          lines.push(`  -H '${prop.name}: <AI_GENERATED>' \\`);
        } else if (prop.default !== undefined && prop.default !== null) {
          lines.push(`  -H '${prop.name}: ${prop.default}' \\`);
        }
      });
    }

    // Add body for non-GET methods
    if (this.tool.method !== 'GET' && this.bodyProperties && this.bodyProperties.length > 0) {
      lines.push(`  -H 'Content-Type: application/json' \\`);
      const bodyPreview = this.generateBodyPreview(this.bodyProperties);
      const bodyJson = JSON.stringify(bodyPreview, null, 2).split('\n').map((line, idx, arr) =>
        idx === arr.length - 1 ? `    ${line}` : `    ${line}`
      ).join('\n');
      lines.push(`  -d '${bodyJson}'`);
    } else {
      // Remove trailing backslash from last line
      if (lines[lines.length - 1].endsWith(' \\')) {
        lines[lines.length - 1] = lines[lines.length - 1].slice(0, -2);
      }
    }

    return lines.join('\n');
  }

  private generateBodyPreview(properties: PropertyDefinition[]): any {
    const result: any = {};

    properties.forEach(prop => {
      if (prop.fillMode === 'llm') {
        result[prop.name] = `<AI_GENERATED_${prop.type.toUpperCase()}>`;
      } else {
        switch (prop.type) {
          case 'object':
            // Always generate from nested properties
            result[prop.name] = prop.properties ? this.generateBodyPreview(prop.properties) : {};
            break;
          case 'array':
            // Use default value if provided, but for object items generate from properties
            if (prop.default !== undefined && Array.isArray(prop.default)) {
              if (prop.items?.type === 'object' && prop.items.properties) {
                // For array of objects, generate each object from properties
                result[prop.name] = prop.default.map(() => this.generateBodyPreview(prop.items!.properties || []));
              } else {
                // For primitive types, use default values as is
                result[prop.name] = prop.default;
              }
            } else if (prop.items?.type === 'object' && prop.items.properties) {
              result[prop.name] = [this.generateBodyPreview(prop.items.properties)];
            } else {
              result[prop.name] = [`<${prop.items?.type || 'item'}>`];
            }
            break;
          default:
            result[prop.name] = prop.default !== undefined ? prop.default : `<${prop.type}>`;
        }
      }
    });

    return result;
  }

  // Copy curl command to clipboard
  async copyCurlToClipboard(): Promise<void> {
    try {
      const curlCommand = this.getCurlPreview();
      await navigator.clipboard.writeText(curlCommand);
      this.notificationService.showSuccess($localize`:@@webhookTool.notify.curlCopied:cURL command copied to clipboard`);
    } catch (err) {
      // Fallback для старых браузеров
      const textArea = document.createElement('textarea');
      textArea.value = this.getCurlPreview();
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      this.notificationService.showSuccess($localize`:@@webhookTool.notify.curlCopied:cURL command copied to clipboard`);
    }
  }

  // TODO: Все методы ниже будут переделаны с использованием OpenAPI схем
  /*
  // Header management methods
  addHeader(): void {
    const newHeader = {
      name: '',
      value: ''
    };
    this.tool.headers = this.tool.headers || [];
    this.tool.headers.push(newHeader);
    this.cdr.markForCheck();
  }

  deleteHeader(index: number): void {
    this.tool.headers = this.tool.headers || [];
    this.tool.headers.splice(index, 1);
    this.cdr.markForCheck();
  }


  onHeaderNameChange(index: number, name: string): void {
    this.tool.headers = this.tool.headers || [];
    if (this.tool.headers[index]) {
      this.tool.headers[index].name = name;
      this.cdr.markForCheck();
    }
  }

  onHeaderValueChange(index: number, value: string): void {
    this.tool.headers = this.tool.headers || [];
    if (this.tool.headers[index]) {
      this.tool.headers[index].value = value;
      this.cdr.markForCheck();
    }
  }

  // Query Parameter management methods
  addQueryParameter(): void {
    const newParam = {
      identifier: '',
      data_type: 'String' as any,
      value_type: 'ConstantValue' as any,
      required: false,
      description: null
    };
    this.tool.query_parameters = this.tool.query_parameters || [];
    this.tool.query_parameters.push(newParam);
    this.cdr.markForCheck();
  }

  deleteQueryParameter(index: number): void {
    this.tool.query_parameters = this.tool.query_parameters || [];
    this.tool.query_parameters.splice(index, 1);
    this.cdr.markForCheck();
  }

  onQueryParameterChange(index: number, field: string, value: any): void {
    this.tool.query_parameters = this.tool.query_parameters || [];
    if (this.tool.query_parameters[index]) {
      (this.tool.query_parameters[index] as any)[field] = value;
      this.cdr.markForCheck();
    }
  }

  // Body parameters management
  onBodyParametersChange(parameters: any[]): void {
    this.tool.body_parameters = parameters;
    this.cdr.markForCheck();
  }

  getQueryParametersPreview(): string {
    if (!this.tool.query_parameters || this.tool.query_parameters.length === 0) {
      return '';
    }

    const baseUrl = this.tool.url || 'https://api.example.com';
    const params: string[] = [];

    this.tool.query_parameters.forEach(param => {
      if (!param.identifier) return;

      let value: string;
      if (param.value_type === 'ConstantValue') {
        // Use the appropriate constant value based on data type
        switch (param.data_type) {
          case 'String':
            value = param.constant_string || '';
            break;
          case 'Integer':
            value = (param.constant_int || 0).toString();
            break;
          case 'Number':
            value = (param.constant_decimal || 0.0).toString();
            break;
          case 'Bool':
            value = (param.constant_bool || false).toString();
            break;
          default:
            value = '';
        }
      } else {
        // LLM Prompt - show placeholder
        value = `<LLM will generate ${param.data_type?.toLowerCase() || 'value'}>`;
      }

      params.push(`${param.identifier}=${value}`);
    });

    return params.length > 0 ? `${baseUrl}?${params.join('&')}` : baseUrl;
  }

  async copyUrlToClipboard(): Promise<void> {
    try {
      const urlString = this.getQueryParametersPreview();
      await navigator.clipboard.writeText(urlString);
      this.notificationService.showSuccess($localize`:@@webhookTool.notify.urlCopied:URL copied to clipboard`);
    } catch (err) {
      // Fallback для старых браузеров
      const textArea = document.createElement('textarea');
      textArea.value = this.getQueryParametersPreview();
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);

      this.notificationService.showSuccess($localize`:@@webhookTool.notify.urlCopied:URL copied to clipboard`);
    }
  }
  */

}